import { UnreachableError } from 'base/helpers';
import { LibraryCache } from 'base/library_cache';
import { SlotInfo } from 'blocks/component_renderer/slots';
import {
  computeNewLocationMarginOverrides,
  marginOverrideToMarginState,
} from 'blocks/component_renderer/slots_margins';
import { observable } from 'mobx';
import { isUserWrittenName } from './ai/page_sections';
import {
  ElementInstance,
  ElementInstanceState,
  Page,
  PageState,
  Section,
  SectionRef,
  SectionRefState,
  SlotOverride,
  Space,
} from './model';
import { findParentSpace } from './model_helpers';
import { SelectionStore } from './selection/store';
import { findOverridableImagesInElementSynchronous } from './styleguide/images';

type AddPageSuggestion = Readonly<{
  type: 'add-page';
  parent: Page | Space;
  index: number;
  page: PageState;
}>;

type AddSectionsSuggestion = Readonly<{
  type: 'add-sections';
  page: Page;
  index: number | 'end';
  sections: readonly SectionRefState[];
  replaceEmptyPageName?: string;
}>;

export type ReplaceComponentSuggestion = Readonly<{
  type: 'replace-component';
  page: Page;
  sref: SectionRef;
  section: Section;
  /** The defaults are used if the section has no name */
  defaultName?: string;
  defaultDescription?: string;
  element: ElementInstanceState | undefined;
  needsToGenerateCopy: boolean;
}>;

type AddElementSuggestion = Readonly<{
  type: 'add-element';
  page: Page;
  sref: SectionRef;
  slotOverride: SlotOverride;
  slotInfo: SlotInfo;
  element: ElementInstanceState;
  index: number;
}>;

export type ReplaceElementSuggestion = Readonly<{
  type: 'replace-element';
  page: Page;
  sref: SectionRef;
  slotOverride: SlotOverride;
  oldElement: ElementInstance;
  newElement: ElementInstanceState;
}>;

export type Suggestion =
  | AddPageSuggestion
  | AddSectionsSuggestion
  | ReplaceComponentSuggestion
  | AddElementSuggestion
  | ReplaceElementSuggestion;

export class SuggestionsStore {
  private suggestionGetters = observable.set<() => Suggestion>([], {
    deep: false,
  });

  private *suggestions(): Generator<Suggestion> {
    for (const getter of this.suggestionGetters) {
      yield getter();
    }
  }

  add(suggestionOrGetter: Suggestion | (() => Suggestion)): () => void {
    const getter =
      typeof suggestionOrGetter === 'function'
        ? suggestionOrGetter
        : () => suggestionOrGetter;
    this.suggestionGetters.add(getter);
    return () => {
      this.suggestionGetters.delete(getter);
    };
  }

  getSuggestedPages(parent: Page | Space) {
    const suggestions: AddPageSuggestion[] = [];
    for (const s of this.suggestions()) {
      if (s.type === 'add-page' && s.parent === parent) {
        suggestions.push(s);
      }
    }
    return suggestions;
  }

  hasSuggestedSections(page: Page) {
    for (const s of this.suggestions()) {
      if (s.type === 'add-sections' && s.page === page) {
        return true;
      }
    }
    return false;
  }

  getSuggestedSections(page: Page, index: number) {
    const suggestions: SectionRefState[] = [];
    const isEnd = index === page.sections.length;
    for (const sug of this.suggestions()) {
      if (
        sug.type === 'add-sections' &&
        sug.page === page &&
        (sug.index === index || (sug.index == 'end' && isEnd))
      ) {
        suggestions.push(...sug.sections);
      }
    }
    return suggestions;
  }

  getElement(section: Section): {
    element: ElementInstance | ElementInstanceState | undefined;
    isSuggestion: boolean;
  } {
    for (const sug of this.suggestions()) {
      if (sug.type === 'replace-component' && sug.section === section) {
        return { element: sug.element, isSuggestion: true };
      }
    }
    return { element: section.element.current, isSuggestion: false };
  }

  getSuggestedElementsToAdd(slotOverride: SlotOverride, index: number) {
    const suggestions: ElementInstanceState[] = [];
    for (const s of this.suggestions()) {
      if (
        s.type === 'add-element' &&
        s.slotOverride === slotOverride &&
        s.index === index
      ) {
        suggestions.push(s.element);
      }
    }
    return suggestions;
  }

  getSuggestedElementReplacement(
    element: ElementInstance,
  ): ElementInstanceState | undefined {
    for (const s of this.suggestions()) {
      if (s.type === 'replace-element' && s.oldElement === element) {
        return s.newElement;
      }
    }
  }
}

export const globalSuggestionsStore = new SuggestionsStore();

function commitAddPageSuggestion(
  suggestion: AddPageSuggestion,
  selection: SelectionStore,
) {
  const { parent, index, page } = suggestion;
  parent.subPages.splice(index, 0, page);
  const newPage = parent.subPages[index];
  const space = findParentSpace(selection.project, newPage) || 'home';
  selection.set({ space, page: newPage });
}
function commitAddSectionsSuggestion(
  suggestion: AddSectionsSuggestion,
  selection: SelectionStore,
) {
  const {
    page,
    sections,
    index: indexOrEnd,
    replaceEmptyPageName,
  } = suggestion;
  if (
    page.sections.length === 0 &&
    !isUserWrittenName(page.name) &&
    replaceEmptyPageName
  ) {
    page.name = replaceEmptyPageName;
  }
  const index = indexOrEnd === 'end' ? page.sections.length : indexOrEnd;
  page.sections.splice(index, 0, ...sections);
  const firstNewSection = page.sections[index];
  const space = findParentSpace(selection.project, page) || 'home';
  selection.set({ space, page, section: firstNewSection });
}
function commitReplaceComponentSuggestion(
  suggestion: ReplaceComponentSuggestion,
  selection: SelectionStore,
  library: LibraryCache,
) {
  const { page, sref, section, element } = suggestion;

  // Grab the new and old element ids before any replacement happens
  const newElementId = element?.id;
  const oldElementId = section.element.current?.id;

  const newImages = element
    ? findOverridableImagesInElementSynchronous(element, library)
    : [];
  const oldImages = section.element.current
    ? findOverridableImagesInElementSynchronous(
        section.element.current,
        library,
      )
    : [];

  // Actually replace the component
  section.element.replace(element);
  section.isComponentOutOfSync = false;
  if (section.name.length === 0) {
    section.name = suggestion.defaultName || '';
    section.description = suggestion.defaultDescription || '';
  }

  // Map the concept scheme overrides from the old to new element id
  if (newElementId != null && oldElementId != null) {
    for (const concept of selection.project.concepts) {
      const schemeOverride =
        concept.schemeOverrideByElementId.get(oldElementId);
      if (schemeOverride != null) {
        // Create the new one and clean up after ourselves
        concept.schemeOverrideByElementId.set(newElementId, schemeOverride);
        concept.schemeOverrideByElementId.delete(oldElementId);
      }
    }

    if (oldImages.length === 1 && newImages.length === 1) {
      const [oldImage] = oldImages;
      const [newImage] = newImages;

      if (
        oldImage.key.startsWith(oldElementId) &&
        newImage.key.startsWith(newElementId)
      ) {
        // Simple case where both the old and new sections each only have one image, and it's on the root element.
        // Map image overrides for each concept.
        for (const concept of selection.project.concepts) {
          const imageRef = concept.images.get(oldImage.key);
          if (imageRef != null) {
            concept.images.set(newImage.key, imageRef.state);
            concept.images.delete(oldImage.key);
          }
        }
      }
    }
  }

  const space = findParentSpace(selection.project, page) || 'home';
  selection.set({ space, page, section: sref });
}
function commitAddElementSuggestion(
  suggestion: AddElementSuggestion,
  selection: SelectionStore,
  library: LibraryCache,
) {
  const { page, sref, slotOverride, slotInfo, element, index } = suggestion;

  slotOverride.items.splice(index, 0, element);

  const marginOverrides = computeNewLocationMarginOverrides({
    slotItems: slotOverride.items,
    slotInfo,
    library,
    index,
  });
  for (const { index, marginOverride } of marginOverrides) {
    slotOverride.items[index].margin =
      marginOverrideToMarginState(marginOverride);
  }

  const space = findParentSpace(selection.project, page) || 'home';
  const newElement = slotOverride.items[index];
  selection.set(
    { space, page, section: sref, element: newElement },
    { disableEnsureVisible: true },
  );
}
function commitReplaceElementSuggestion(
  suggestion: ReplaceElementSuggestion,
  selection: SelectionStore,
) {
  const {
    page,
    sref,
    slotOverride,
    oldElement,
    newElement: newElementState,
  } = suggestion;

  const index = slotOverride.items.findIndex((el) => el === oldElement);
  if (index < 0) return;

  slotOverride.items.splice(index, 1, newElementState);

  const space = findParentSpace(selection.project, page) || 'home';
  selection.set(
    { space, page, section: sref, element: slotOverride.items[index] },
    { disableEnsureVisible: true },
  );
}
export function commitSuggestion(
  suggestion: Suggestion,
  selection: SelectionStore,
  library: LibraryCache,
) {
  switch (suggestion.type) {
    case 'add-page':
      commitAddPageSuggestion(suggestion, selection);
      break;
    case 'add-sections':
      commitAddSectionsSuggestion(suggestion, selection);
      break;
    case 'replace-component':
      commitReplaceComponentSuggestion(suggestion, selection, library);
      break;
    case 'add-element':
      commitAddElementSuggestion(suggestion, selection, library);
      break;
    case 'replace-element':
      commitReplaceElementSuggestion(suggestion, selection);
      break;
    default:
      throw new UnreachableError(suggestion);
  }
}
